import React, { useState } from 'react';
import {
  BarChart3,
  TrendingUp,
  ArrowUpDown,
  Target,
  Clock,
  Shield,
  Brain,
  Filter,
  Download
} from 'lucide-react';
import Button from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';

const ComparativeAnalysis = ({ reports = [] }) => {
  const [selectedMetric, setSelectedMetric] = useState('accuracy');
  const [comparisonType, setComparisonType] = useState('bar');

  if (!reports || reports.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Data for Comparison</h3>
          <p className="text-muted-foreground">
            Select multiple evaluation reports to see comparative analysis.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Prepare comparison data
  const comparisonData = reports.map((report, index) => ({
    name: `Report ${index + 1}`,
    reportId: report.id,
    date: new Date(report.generated_at).toLocaleDateString(),
    accuracy: report.metrics.accuracy.score,
    latency: report.metrics.latency.average_ms,
    security: report.metrics.security.score,
    contentSafety: report.metrics.offense.content_safety_score,
    reliability: report.metrics.hallucination.factual_accuracy_score,
    overallScore: report.overall_score
  }));

  // Prepare radar chart data
  const radarData = [
    {
      metric: 'Accuracy',
      ...reports.reduce((acc, report, index) => {
        acc[`Report ${index + 1}`] = report.metrics.accuracy.score;
        return acc;
      }, {})
    },
    {
      metric: 'Security',
      ...reports.reduce((acc, report, index) => {
        acc[`Report ${index + 1}`] = report.metrics.security.score;
        return acc;
      }, {})
    },
    {
      metric: 'Content Safety',
      ...reports.reduce((acc, report, index) => {
        acc[`Report ${index + 1}`] = report.metrics.offense.content_safety_score;
        return acc;
      }, {})
    },
    {
      metric: 'Reliability',
      ...reports.reduce((acc, report, index) => {
        acc[`Report ${index + 1}`] = report.metrics.hallucination.factual_accuracy_score;
        return acc;
      }, {})
    }
  ];

  // Prepare trend data
  const trendData = reports.map((report, index) => ({
    date: new Date(report.generated_at).toLocaleDateString(),
    accuracy: report.metrics.accuracy.score,
    security: report.metrics.security.score,
    reliability: report.metrics.hallucination.factual_accuracy_score,
    overallScore: report.overall_score
  })).sort((a, b) => new Date(a.date) - new Date(b.date));

  const handleExportComparison = () => {
    const csvData = comparisonData.map(item => ({
      Report: item.name,
      Date: item.date,
      'Overall Score': item.overallScore,
      'Accuracy (%)': item.accuracy,
      'Latency (ms)': item.latency,
      'Security (%)': item.security,
      'Content Safety (%)': item.contentSafety,
      'Reliability (%)': item.reliability
    }));

    const csvContent = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `comparative-analysis-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Comparative Analysis</h2>
          <p className="text-muted-foreground mt-1">
            Compare performance across {reports.length} evaluation reports
          </p>
        </div>
        <Button onClick={handleExportComparison}>
          <Download className="w-4 h-4 mr-2" />
          Export Comparison
        </Button>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-4 p-4 bg-secondary/20 rounded-lg">
        <div className="flex items-center space-x-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <select
            value={selectedMetric}
            onChange={(e) => setSelectedMetric(e.target.value)}
            className="px-3 py-2 border border-input rounded-md bg-background text-foreground"
          >
            <option value="accuracy">Accuracy</option>
            <option value="security">Security</option>
            <option value="contentSafety">Content Safety</option>
            <option value="reliability">Reliability</option>
            <option value="overallScore">Overall Score</option>
          </select>
        </div>

        <div className="flex items-center space-x-2">
          <BarChart3 className="w-4 h-4 text-muted-foreground" />
          <select
            value={comparisonType}
            onChange={(e) => setComparisonType(e.target.value)}
            className="px-3 py-2 border border-input rounded-md bg-background text-foreground"
          >
            <option value="bar">Bar Chart</option>
            <option value="line">Line Chart</option>
            <option value="radar">Radar Chart</option>
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Best Accuracy</p>
                <p className="text-2xl font-bold text-green-600">
                  {Math.max(...comparisonData.map(d => d.accuracy))}%
                </p>
              </div>
              <Target className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Best Latency</p>
                <p className="text-2xl font-bold text-blue-600">
                  {Math.min(...comparisonData.map(d => d.latency))}ms
                </p>
              </div>
              <Clock className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Best Security</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {Math.max(...comparisonData.map(d => d.security))}%
                </p>
              </div>
              <Shield className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Best Reliability</p>
                <p className="text-2xl font-bold text-purple-600">
                  {Math.max(...comparisonData.map(d => d.reliability))}%
                </p>
              </div>
              <Brain className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Comparison Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <ArrowUpDown className="w-5 h-5" />
            <span>Performance Comparison</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              {comparisonType === 'bar' ? (
                <BarChart data={comparisonData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
                            <p className="font-medium">{label}</p>
                            <p className="text-sm text-muted-foreground">Date: {data.date}</p>
                            <p className="text-sm">
                              {selectedMetric === 'accuracy' && `Accuracy: ${data.accuracy}%`}
                              {selectedMetric === 'security' && `Security: ${data.security}%`}
                              {selectedMetric === 'contentSafety' && `Content Safety: ${data.contentSafety}%`}
                              {selectedMetric === 'reliability' && `Reliability: ${data.reliability}%`}
                              {selectedMetric === 'overallScore' && `Overall Score: ${data.overallScore}%`}
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey={selectedMetric} fill="#8884d8" />
                </BarChart>
              ) : comparisonType === 'line' ? (
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="accuracy" stroke="#10B981" strokeWidth={2} />
                  <Line type="monotone" dataKey="security" stroke="#F59E0B" strokeWidth={2} />
                  <Line type="monotone" dataKey="reliability" stroke="#8B5CF6" strokeWidth={2} />
                  <Line type="monotone" dataKey="overallScore" stroke="#3B82F6" strokeWidth={2} />
                </LineChart>
              ) : (
                <RadarChart data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="metric" />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} />
                  {reports.map((_, index) => (
                    <Radar
                      key={index}
                      name={`Report ${index + 1}`}
                      dataKey={`Report ${index + 1}`}
                      stroke={`hsl(${index * 60}, 70%, 50%)`}
                      fill={`hsl(${index * 60}, 70%, 50%)`}
                      fillOpacity={0.1}
                      strokeWidth={2}
                    />
                  ))}
                  <Tooltip />
                  <Legend />
                </RadarChart>
              )}
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Comparison Table */}
      <Card>
        <CardHeader>
          <CardTitle>Detailed Metrics Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3">Report</th>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Overall Score</th>
                  <th className="text-left p-3">Accuracy</th>
                  <th className="text-left p-3">Latency</th>
                  <th className="text-left p-3">Security</th>
                  <th className="text-left p-3">Content Safety</th>
                  <th className="text-left p-3">Reliability</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((item, index) => (
                  <tr key={index} className="border-b border-border hover:bg-accent/50">
                    <td className="p-3 font-medium">{item.name}</td>
                    <td className="p-3 text-sm text-muted-foreground">{item.date}</td>
                    <td className="p-3">
                      <span className="font-medium">{item.overallScore}%</span>
                    </td>
                    <td className="p-3">
                      <span className="font-medium">{item.accuracy}%</span>
                    </td>
                    <td className="p-3">
                      <span className="font-medium">{item.latency}ms</span>
                    </td>
                    <td className="p-3">
                      <span className="font-medium">{item.security}%</span>
                    </td>
                    <td className="p-3">
                      <span className="font-medium">{item.contentSafety}%</span>
                    </td>
                    <td className="p-3">
                      <span className="font-medium">{item.reliability}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Performance Insights */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="w-5 h-5" />
            <span>Performance Insights</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-lg font-medium mb-3">Improvements</h4>
              <div className="space-y-2">
                {comparisonData.length > 1 && (
                  <>
                    {comparisonData[comparisonData.length - 1].accuracy > comparisonData[0].accuracy && (
                      <div className="flex items-center space-x-2 text-green-600">
                        <TrendingUp className="w-4 h-4" />
                        <span className="text-sm">Accuracy improved by {(comparisonData[comparisonData.length - 1].accuracy - comparisonData[0].accuracy).toFixed(1)}%</span>
                      </div>
                    )}
                    {comparisonData[comparisonData.length - 1].security > comparisonData[0].security && (
                      <div className="flex items-center space-x-2 text-green-600">
                        <TrendingUp className="w-4 h-4" />
                        <span className="text-sm">Security improved by {(comparisonData[comparisonData.length - 1].security - comparisonData[0].security).toFixed(1)}%</span>
                      </div>
                    )}
                    {comparisonData[comparisonData.length - 1].latency < comparisonData[0].latency && (
                      <div className="flex items-center space-x-2 text-green-600">
                        <TrendingUp className="w-4 h-4" />
                        <span className="text-sm">Latency improved by {(comparisonData[0].latency - comparisonData[comparisonData.length - 1].latency).toFixed(0)}ms</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div>
              <h4 className="text-lg font-medium mb-3">Areas for Focus</h4>
              <div className="space-y-2">
                {Math.min(...comparisonData.map(d => d.accuracy)) < 80 && (
                  <div className="flex items-center space-x-2 text-orange-600">
                    <Target className="w-4 h-4" />
                    <span className="text-sm">Accuracy needs improvement (lowest: {Math.min(...comparisonData.map(d => d.accuracy))}%)</span>
                  </div>
                )}
                {Math.max(...comparisonData.map(d => d.latency)) > 3000 && (
                  <div className="flex items-center space-x-2 text-orange-600">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">Latency optimization needed (highest: {Math.max(...comparisonData.map(d => d.latency))}ms)</span>
                  </div>
                )}
                {Math.min(...comparisonData.map(d => d.security)) < 90 && (
                  <div className="flex items-center space-x-2 text-orange-600">
                    <Shield className="w-4 h-4" />
                    <span className="text-sm">Security enhancements recommended (lowest: {Math.min(...comparisonData.map(d => d.security))}%)</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ComparativeAnalysis;
